import { Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  summarizeIngestionOutcomes,
  type IngestJobUi,
} from "../../services/ragDocumentApi";
import type { DocumentRowUi } from "../../types/documentManagementUiTypes";

function ingestionDoneSummaryMessage(
  t: TFunction,
  processed: number,
  failed: number,
  removed: number,
  total: number,
  jobsLength: number
): string {
  const problemCount = failed + removed;

  if (problemCount === 0) {
    return t("opva.dmIngestionDoneSummary", {
      defaultValue: "All {{total}} document(s) processed successfully.",
      total: total || jobsLength,
    });
  }

  if (processed > 0) {
    if (removed > 0 && failed > 0) {
      return t("opva.dmIngestionDoneSummaryPartialSuccessMixed", {
        defaultValue:
          "{{processed}} document(s) processed successfully. {{failed}} document(s) could not be processed. {{removed}} document(s) removed because the file had no usable content.",
        processed,
        failed,
        removed,
      });
    }
    if (removed > 0) {
      return t("opva.dmIngestionDoneSummaryPartialSuccessWithRemovals", {
        defaultValue:
          "{{processed}} document(s) processed successfully. {{removed}} document(s) removed because the file had no usable content.",
        processed,
        removed,
      });
    }
    return t("opva.dmIngestionDoneSummaryPartialSuccessWithFailures", {
      defaultValue:
        "{{processed}} document(s) processed successfully. {{failed}} document(s) could not be processed.",
      processed,
      failed,
    });
  }

  if (removed > 0) {
    return t("opva.dmIngestionDoneSummaryWithRemovals", {
      defaultValue:
        "{{removed}} document(s) removed because the file had no usable content.",
      removed,
    });
  }

  return t("opva.dmIngestionDoneSummaryWithFailures", {
    defaultValue: "{{failed}} document(s) could not be processed.",
    failed,
  });
}

export type DocumentIngestionDoneProps = {
  jobs: IngestJobUi[];
  documents: DocumentRowUi[];
  onUploadMore: () => void;
  onViewDocuments: () => void;
};

export function DocumentIngestionDone({
  jobs,
  documents,
  onUploadMore,
  onViewDocuments,
}: DocumentIngestionDoneProps) {
  const { t } = useTranslation();
  const { processed, failed, removed, total } = summarizeIngestionOutcomes(
    jobs,
    documents
  );
  const problemCount = failed + removed;
  const summaryMessage = ingestionDoneSummaryMessage(
    t,
    processed,
    failed,
    removed,
    total,
    jobs.length
  );

  return (
    <Box component="section" className="dm-ingest-panel dm-ingest-done">
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {problemCount > 0
          ? t("opva.dmIngestionDoneTitleWithFailures", {
              defaultValue: "Processing finished with errors",
            })
          : t("opva.dmIngestionDoneTitle", {
              defaultValue: "Processing complete",
            })}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {summaryMessage}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        <Button variant="contained" onClick={onUploadMore}>
          {t("opva.dmUploadMore", { defaultValue: "Upload more documents" })}
        </Button>
        <Button variant="outlined" onClick={onViewDocuments}>
          {t("opva.dmViewDocuments", { defaultValue: "View documents" })}
        </Button>
      </Box>
    </Box>
  );
}
